import { forwardRef, type ReactNode } from 'react';
import { VisuallyHidden } from '../visually-hidden/visually-hidden.js';

/**
 * Chip — the one pill the product draws every chip from.
 *
 * Corpus, `DB` unless stated: `1841-1843` the static
 * `chip(fg, bg)` factory and its `chipOpen`/`chipUrgent`/`chipClock`/
 * `chipQuoted`/`chipNeutral` aliases; `2555-2559` the `TONES` table those
 * aliases collapse to (red, amber, green, slate, grey); `2063-2067`
 * `selChip(on, col, bg)`, the bordered pressable chip; `1893-1897` the
 * borderless queue filters; `1750-1758` the language segment on its grey track
 * and `1766-1770` the bordered role chips beside it.
 * `PT:93-96` draws a second, smaller pill (11px, 0.03em
 * tracking, its weight parameterised) that this atom does not offer: it has no
 * token, and no product surface asks for it.
 *
 * `chip.css` records, value by value, which corpus numbers have no token and
 * what was drawn instead. Two decisions are this file's rather than the CSS's:
 *
 * **Presence of `onPress` is what makes it a control.** Without one the chip is
 * a `<span>` with no role and no tab stop, because a label that reacts to
 * nothing but is announced as a button is a promise to a screen-reader user
 * that the page does not keep. With one it is a real `<button type="button">`,
 * so Space and Enter work without a keydown handler and the global
 * `:focus-visible` ring in `tokens.css` is its visible focus.
 *
 * **The "on" treatment is selected by `aria-pressed`, not by a class.** A chip
 * cannot then look pressed while announcing itself unpressed — the two states
 * are the same state. `selected` left undefined emits no `aria-pressed` at all,
 * which is the corpus's other pressable chip: `DB:2245` presses
 * to do something rather than to turn something on, and a toggle role there
 * would announce a state it does not have.
 *
 * Every string a person reads arrives as a prop, including
 * `leadingGlyph` — the corpus prefixes a ✓ or ✕ at `DB:2015`, and
 * it is hidden from assistive technology here because `label` already says what
 * it means. Colour is never the only carrier: the tone repeats the words.
 */
export interface ChipProps {
  /** The visible text. A blank one renders nothing — see below. */
  readonly label: string;
  readonly variant?: 'tone' | 'select' | 'fill' | 'segment' | 'outline';
  /**
   * Solid by default. `dashed` is the "fills the form" treatment — the same
   * affordance `UploadDropzone` and `PhotoGrid`'s add cell draw for an empty
   * slot waiting to be filled, offered here for a chip used the same way (an
   * unset filter, an unclaimed tag). Applies to the bordered variants
   * (`select`, `outline`); a variant with no border of its own ignores it,
   * because a dashed edge with nothing to dash is not a smaller version of the
   * affordance, it is a no-op that looks like a bug report waiting to happen.
   */
  readonly borderStyle?: 'solid' | 'dashed';
  /**
   * Extra context appended to the visible `label`, for a pressable chip whose
   * label reads ambiguously on its own — a filter or toggle chip that just
   * says "Urgent" or "Plumber" and needs the state or the group said aloud
   * too. Composed as a hidden suffix rather than set as a raw `aria-label`
   * override, the same reason and the same shape `Wordmark`'s `homeLabel`
   * already uses: `aria-label` replaces an element's whole content in the
   * accessible name, so a caller supplying a fuller description with no
   * mechanical requirement to repeat the visible word could silently drop it
   * — a speech-control user saying "Plumber" would then have nothing to
   * target (WCAG 2.2 SC 2.5.3). Only wired to the pressable `<button>` form:
   * ARIA forbids `aria-label` on a roleless element, so on the static
   * `<span>` — which has no role — it would be an accessible name that
   * reaches nobody, and is silently ignored there rather than shipped as a
   * violation.
   */
  readonly ariaLabel?: string;
  /**
   * The mark joining `label` to `ariaLabel` — an em dash by default, which is
   * this library's own choice and not one every script shares: `TimelineEventRow`'s
   * `attributionSeparator` exists for the identical reason ("not every script
   * uses U+00B7"), and this is that same question for this join. Overridable
   * rather than removable: the join has to be SOME non-whitespace mark, because
   * the accessible-name computation trims each named element's own subtree
   * before concatenation — a leading plain space here would vanish and glue
   * `label` and `ariaLabel` into one unbroken word, which is worse than the
   * wrong-language mark this exists to let a caller correct.
   */
  readonly ariaLabelSeparator?: string;
  /**
   * The language `ariaLabel` is written in, as a BCP 47 tag, when it is not
   * the page's own. A language switcher names its "ES" cell "Español", and
   * without a tag an English screen-reader voice mispronounces that endonym.
   * Applied to the `ariaLabel` text only: the separator is the page's own
   * punctuation. Blank counts as absent.
   */
  readonly ariaLabelLang?: string | undefined;
  /**
   * The `TONES` keys at `DB:2557`, and nothing else. Read by
   * `variant="tone"` for its whole colour pair, and by `variant="select"` for
   * the one accent the corpus draws — `red`, the hazard chip at
   * `DB:2073`. Every other pairing ignores it. A pressed `tone`
   * chip gives its colour up for the dark fill, as `select` and `outline` give
   * theirs up, so the tone is what reads at rest.
   */
  readonly tone?: 'green' | 'red' | 'amber' | 'slate' | 'grey';
  /** Undefined means "not a toggle". Ignored entirely when there is no `onPress`. */
  readonly selected?: boolean;
  readonly onPress?: () => void;
  /** Pressable chips only. Kept focusable, so it can still be found and read. */
  readonly disabled?: boolean;
  readonly leadingGlyph?: ReactNode;
  readonly id?: string;
  /**
   * Escape hatch for a proper radiogroup owner, and nothing else reaches it —
   * see `RadioChoiceGroup`, the organism that owns an exclusive choice set's
   * roving focus and arrow keys. `aria-pressed` and `role="radio"`/
   * `aria-checked` cannot both describe one control, so
   * setting this swaps the emitted attribute; it does not make a plain chip a
   * radio on its own, because pairing this with no `tabIndex` from an owner
   * that also manages roving focus and arrow keys ships the announcement
   * without the behaviour ARIA promises alongside it.
   */
  readonly role?: 'radio';
  /** Paired with `role`, for a caller managing roving `tabindex` itself. */
  readonly tabIndex?: number;
  /**
   * The id of text that describes this control, for the pressable form only.
   * `AccessWindow` uses it so a read-only pets chip announces the recorded
   * answer in words with the control, not only as a neighbouring span a
   * reader moving between controls never reaches. Blank counts as absent.
   */
  readonly describedById?: string | undefined;
}

/**
 * Forwards to the `<button>` on the pressable form only — `RadioChoiceGroup`
 * is the caller, moving focus itself for the roving `tabindex` a radiogroup
 * needs. The static `<span>` form takes no ref: it holds no control for a
 * caller to focus.
 */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  {
    label,
    variant = 'tone',
    borderStyle = 'solid',
    ariaLabel,
    ariaLabelSeparator,
    ariaLabelLang,
    tone = 'grey',
    selected,
    onPress,
    disabled = false,
    leadingGlyph,
    id,
    role,
    tabIndex,
    describedById,
  }: ChipProps,
  ref,
) {
  // A blank label leaves a coloured shape carrying the whole message to a
  // sighted reader and silence to everyone else — rule 5's exact failure. The
  // component may not invent the missing word, so it draws nothing at all;
  // `StatusChip` and `SeverityChip` already decided this and say why in full.
  if (label.trim() === '') return null;

  const classes = [
    'rp-chip',
    `rp-chip--${variant}`,
    variant === 'tone' ? `rp-chip--${tone}` : undefined,
    variant === 'select' && tone === 'red' ? 'rp-chip--danger' : undefined,
    borderStyle === 'dashed' ? 'rp-chip--dashed' : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {leadingGlyph === undefined ? null : (
        <span aria-hidden="true" className="rp-chip-glyph">
          {leadingGlyph}
        </span>
      )}
      {label}
    </>
  );

  if (onPress === undefined) {
    return (
      <span id={id} className={classes}>
        {content}
      </span>
    );
  }

  const namedAriaLabel = (ariaLabel ?? '').trim();
  const separator = (ariaLabelSeparator ?? '—').trim() || '—';
  const namedLang = (ariaLabelLang ?? '').trim();

  return (
    <button
      ref={ref}
      id={id}
      // HTML defaults a button to `submit`; a filter chip inside the report
      // form would then lodge a half-finished repair request when pressed.
      type="button"
      className={classes}
      role={role}
      aria-pressed={role === 'radio' ? undefined : selected}
      aria-checked={role === 'radio' ? selected : undefined}
      aria-disabled={disabled ? true : undefined}
      aria-describedby={(describedById ?? '').trim() === '' ? undefined : describedById}
      onClick={disabled ? undefined : onPress}
      tabIndex={tabIndex}
    >
      {content}
      {namedAriaLabel === '' ? null : (
        // The separator opens with the mark rather than a leading space, the
        // same reason `Wordmark`'s does: the accessible-name computation
        // trims each named element's own subtree before appending it to its
        // parent's, so a leading space here would be stripped and never
        // reach the reader at all.
        //
        // The name's own language wraps the name alone, so the separator is
        // not voiced in the endonym's language.
        <VisuallyHidden>
          {`${separator} `}
          {namedLang === '' ? namedAriaLabel : <span lang={namedLang}>{namedAriaLabel}</span>}
        </VisuallyHidden>
      )}
    </button>
  );
});
